import React, { useEffect, useState } from 'react';
import { getRooms } from '../../../lib/api';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { Zap, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { useManagerPortalConfig } from '../manager/useManagerPortalConfig';

const SALES_POINT_TYPES = ['Kassa', 'Theke', 'Lieferung', 'Take-away', 'Sonstiges'];

export default function AdminSalesPoints() {
  const { portalConfig, isLoading, savePortalConfig } = useManagerPortalConfig();
  const [rooms, setRooms] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', type: 'Kassa', room_id: '', active: true });

  useEffect(() => {
    getRooms().then(setRooms).catch(() => setRooms([]));
  }, []);

  const salesPoints = portalConfig?.salesPoints || [];
  const roomName = (roomId) => rooms.find((r) => r.id === roomId)?.name || '—';

  const openDialog = (salesPoint = null) => {
    setEditing(salesPoint);
    setForm(salesPoint ? { ...salesPoint } : { name: '', type: 'Kassa', room_id: '', active: true });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    const next = editing
      ? salesPoints.map((sp) => (sp.id === editing.id ? { ...sp, ...form } : sp))
      : [...salesPoints, { id: `sp-${Date.now()}`, ...form }];
    await savePortalConfig(
      (prev) => ({ ...prev, salesPoints: next }),
      editing ? 'Verkaufspunkt aktualisiert' : 'Verkaufspunkt erstellt',
    );
    setShowDialog(false);
  };

  const handleDelete = async (salesPoint) => {
    if (!window.confirm(`Verkaufspunkt "${salesPoint.name}" wirklich löschen?`)) return;
    const next = salesPoints.filter((sp) => sp.id !== salesPoint.id);
    await savePortalConfig((prev) => ({ ...prev, salesPoints: next }), 'Verkaufspunkt gelöscht');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Verkaufspunkte</h1>
          <p className="text-slate-500">{salesPoints.length} Kassenpunkte / Terminals</p>
        </div>
        <Button className="bg-[#1a2f38] hover:bg-[#2a4048]" onClick={() => openDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Neuer Verkaufspunkt
        </Button>
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Verkaufspunkt</TableHead>
                <TableHead>Typ</TableHead>
                <TableHead>Raum</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="w-[100px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {salesPoints.map((sp) => (
                <TableRow key={sp.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <Zap className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="font-medium">{sp.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{sp.type}</TableCell>
                  <TableCell className="text-slate-600">{roomName(sp.room_id)}</TableCell>
                  <TableCell className="text-center">
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${sp.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {sp.active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openDialog(sp)}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(sp)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {salesPoints.length === 0 && (
            <div className="text-center py-12">
              <Zap className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500">Keine Verkaufspunkte vorhanden</p>
              <Button className="mt-4 bg-[#1a2f38]" onClick={() => openDialog()}>
                Ersten Verkaufspunkt erstellen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Verkaufspunkt bearbeiten' : 'Neuer Verkaufspunkt'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="z.B. Theke, Kassa 1, Lieferservice"
                autoFocus
              />
            </div>
            <div>
              <Label>Typ</Label>
              <Select value={form.type} onValueChange={(value) => setForm({ ...form, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SALES_POINT_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Raum / Standort</Label>
              <Select value={form.room_id || '__none__'} onValueChange={(value) => setForm({ ...form, room_id: value === '__none__' ? '' : value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Kein Raum zugeordnet" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Kein Raum zugeordnet</SelectItem>
                  {rooms.map((room) => (
                    <SelectItem key={room.id} value={room.id}>{room.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label>Aktiv</Label>
              <Switch checked={form.active} onCheckedChange={(checked) => setForm({ ...form, active: checked })} />
            </div>
            <Button onClick={handleSave} className="w-full bg-[#1a2f38]">
              {editing ? 'Speichern' : 'Erstellen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
